/**
 * NIGHTBOUND - typed browser API client.
 *
 * The client never computes rewards. It sends an *intent* (which food, which
 * discipline) and renders the authoritative view the server returns. Sessions
 * live in an httpOnly cookie, so every request is sent with credentials and no
 * token is ever stored in JavaScript.
 */

import type {
  ActionResponse,
  ApiErrorBody,
  AuthResponse,
  CatalogResponse,
  CreatureView,
  InventoryEntryView,
  MeResponse,
  UserView,
} from '../../shared/types'

/** The player's real UTC offset in minutes (the sign the server expects). */
export function clientTimezoneOffsetMinutes(): number {
  return -new Date().getTimezoneOffset()
}

export class ApiError extends Error {
  readonly code: ApiErrorBody['error']['code'] | 'NETWORK'
  readonly status: number
  readonly details: Record<string, unknown>

  constructor(
    code: ApiErrorBody['error']['code'] | 'NETWORK',
    message: string,
    status: number,
    details: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.details = details
  }

  /** Cooldown responses carry the remaining seconds. */
  get secondsRemaining(): number | undefined {
    const value = this.details.secondsRemaining
    return typeof value === 'number' ? value : undefined
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response: Response
  try {
    response = await fetch(path, {
      method: options.method ?? 'GET',
      credentials: 'include',
      headers: options.body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      ...(options.signal ? { signal: options.signal } : {}),
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ApiError('NETWORK', 'Could not reach the shelter. Check your connection.', 0)
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  let parsed: unknown = undefined
  if (text) {
    try {
      parsed = JSON.parse(text)
    } catch {
      parsed = undefined
    }
  }

  if (!response.ok) {
    const body = parsed as ApiErrorBody | undefined
    const error = body?.error
    throw new ApiError(
      error?.code ?? 'INTERNAL',
      error?.message ?? `Request failed (${response.status}).`,
      response.status,
      error?.details ?? {},
    )
  }

  return parsed as T
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export const api = {
  health: () => request<{ ok: boolean; service: string; env: string; debugTools: boolean; serverTime: string }>('/api/health'),

  catalog: () => request<CatalogResponse>('/api/catalog'),

  register: (input: { email: string; username: string; password: string }) =>
    request<AuthResponse>('/api/auth/register', {
      method: 'POST',
      body: { ...input, timezoneOffsetMinutes: clientTimezoneOffsetMinutes() },
    }),

  login: (input: { email: string; password: string }) =>
    request<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: { ...input, timezoneOffsetMinutes: clientTimezoneOffsetMinutes() },
    }),

  logout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),

  me: () => request<MeResponse>('/api/auth/me'),

  getCreature: (signal?: AbortSignal) => request<{ creature: CreatureView }>('/api/creature', { signal }),

  createCreature: (input: { species: string; name: string }) =>
    request<{ creature: CreatureView }>('/api/creature', { method: 'POST', body: input }),

  interact: () => request<ActionResponse>('/api/creature/interact', { method: 'POST' }),

  feed: (itemKey: string) =>
    request<ActionResponse>('/api/actions/feed', { method: 'POST', body: { itemKey } }),

  clean: () => request<ActionResponse>('/api/actions/clean', { method: 'POST' }),

  play: () => request<ActionResponse>('/api/actions/play', { method: 'POST' }),

  train: (discipline: string) =>
    request<ActionResponse>('/api/actions/train', { method: 'POST', body: { discipline } }),

  sleep: () => request<ActionResponse>('/api/actions/sleep', { method: 'POST' }),

  wake: () => request<ActionResponse>('/api/actions/wake', { method: 'POST' }),

  inventory: () => request<{ inventory: InventoryEntryView[] }>('/api/inventory'),

  settings: () => request<{ user: UserView }>('/api/settings'),

  updateSettings: (patch: Partial<{ soundEnabled: boolean; reducedMotion: boolean; ambienceEnabled: boolean; timezoneOffsetMinutes: number }>) =>
    request<{ user: UserView }>('/api/settings', { method: 'PATCH', body: patch }),

  devStatus: () => request<{ enabled: boolean; timeOffsetMs: number; timeOffsetHours: number }>('/api/dev/status'),

  devAdvance: (hours: number) =>
    request<{ creature: CreatureView; timeOffsetHours: number }>('/api/dev/advance', {
      method: 'POST',
      body: { hours },
    }),

  devSetNeeds: (patch: Record<string, number>) =>
    request<{ creature: CreatureView; timeOffsetHours: number }>('/api/dev/needs', {
      method: 'POST',
      body: patch,
    }),

  devSetMood: (mood: string) =>
    request<{ creature: CreatureView; timeOffsetHours: number }>('/api/dev/mood', {
      method: 'POST',
      body: { mood },
    }),

  devSetSleep: (sleeping: boolean) =>
    request<{ creature: CreatureView; timeOffsetHours: number }>('/api/dev/sleep', {
      method: 'POST',
      body: { sleeping },
    }),

  devClearCooldowns: () =>
    request<{ creature: CreatureView; timeOffsetHours: number }>('/api/dev/clear-cooldowns', {
      method: 'POST',
    }),

  devResetTime: () =>
    request<{ creature: CreatureView; timeOffsetHours: number }>('/api/dev/reset-time', {
      method: 'POST',
    }),
}

export type { ActionResponse, AuthResponse, CatalogResponse, CreatureView, InventoryEntryView, MeResponse, UserView }
